// H2.3 WO#3a §GridContext — SHELL-side composition of the canonical
// viewer context state with route/purchase state. The viewer emits
// only truth it owns (`ViewerContextState`, see
// viewer/context-state.ts). The shell composes that truth with the
// route + the committed rectangle (already flowing through the
// existing `onCommitSelection` data channel) to derive the wider
// `GridContext` that drives what the shell renders (DetailPanel vs
// PurchasePanel).
//
// Devil-review boundary:
//   * `purchase` NEVER appears in `ViewerContextState` — the viewer
//     does not have route authority.
//   * The shell holds `purchase` and derives it exclusively here.
//   * No business data is duplicated: `selection` inside `purchase`
//     is the SAME `GridSelection` payload the viewer emitted via
//     `onCommitSelection`.

import type { GridSelection } from "../lib/grid-selection";
import type { PrimaryView } from "./routing";
import type { ViewerContextState } from "../viewer/context-state";

export type GridContext =
  | ViewerContextState
  | { kind: "purchase"; selection: GridSelection };

export interface DeriveGridContextInput {
  view: PrimaryView;
  viewer: ViewerContextState;
  committedSelection: GridSelection | null;
}

/**
 * Composition rule:
 *   * A committed rectangle (only produced while the Grid is in Select
 *     mode) ALWAYS surfaces the selection preview → `purchase`,
 *     regardless of route. This lets the user draw a rectangle from
 *     the normal Grid via the Select toggle and immediately see the
 *     dimensions / slots / quote in the Context panel, exactly like the
 *     `#purchase` deep-link flow.
 *   * With no committed rectangle → forward the viewer state unchanged.
 *
 * This function is pure and deterministic; it is exercised by
 * `frontend/tests/context-state.test.ts`.
 */
export function deriveGridContext(input: DeriveGridContextInput): GridContext {
  if (input.committedSelection) {
    return { kind: "purchase", selection: input.committedSelection };
  }
  return input.viewer;
}

/**
 * Identity key for the mobile BottomSheet dismiss reset (WO#3a
 * validation round §2). Returns a stable string identity when the
 * shell has a canonical selection to render (`parcel` / `purchase`),
 * or `null` otherwise. The key MUST be stable across structural
 * refreshes of the SAME selection (e.g. `as_of_block` bumping,
 * `reconciled` flipping) so that a background refresh does NOT
 * re-open a sheet the user has explicitly dismissed. Conversely it
 * MUST change when the user picks a different parcel / commits a
 * different rectangle, so the sheet re-opens on the new selection.
 */
export function gridContextIdentity(g: GridContext): string | null {
  if (g.kind === "parcel") return `parcel:${g.detail.parcel.id}`;
  if (g.kind === "purchase") {
    const s = g.selection;
    return `purchase:${s.x},${s.y},${s.w},${s.h}`;
  }
  return null;
}
